import React, { useEffect, useState } from 'react';
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@mui/icons-material/Search';
import Icon from '@mdi/react'
import { mdiTrashCanOutline } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import SubmittableSearchBar from '../SubmittableSearchBar';

import LocationsTreeNode from './LocationsTreeNode';
import './LocationsPicker.scss';


const createLocTrees = (nodes, selectedNodes) => {
    const nodesList = Object.values(nodes);
    const roots = [];

    nodesList.forEach(x => {
        x.children = x.childrenIds.map(y => nodes[y]);
        x.parentNode = nodes[x.parentNodeId];
        x.deepChildren = x.deepChildrenIds.map(y => nodes[y]);
        x.isExpanded = false;
        x.isChecked = false;
        x.isIndeterminate = false;
        x.isSelected = selectedNodes.includes(x.nodeId);
        x.searchHelper = x.orgPath.toLowerCase()
        x.isSearchHighlight = false;
        if (x.isRoot) {
            roots.push(x);
        }
    });

    nodesList.filter(x => x.isSelected)
        .forEach(x => {
            x.isChecked = true;
            checkAllDeepChildren(x, selectedNodes);
            recursiveCheckParents(x, selectedNodes);
        })

    return roots;
}

const removeNodeFromSelection = (node, selectedNodes) => {
    const index = selectedNodes.indexOf(node.nodeId);
    if (index > -1) {
        selectedNodes.splice(index, 1);
    }
    node.isSelected = false;
}

const checkAllDeepChildren = (node, selectedNodes) => {
    if (!node.deepChildren || !node.deepChildren.length) {
        return;
    }

    node.deepChildren.forEach(x => {
        x.isChecked = true;
        if (x.isSelected) {
            removeNodeFromSelection(x, selectedNodes);
        }
    });
}

const uncheckAllDeepChildren = (node, selectedNodes) => {
    if (!node.deepChildren || !node.deepChildren.length) {
        return;
    }
    node.deepChildren.forEach(x => {
        x.isChecked = false;
        x.isIndeterminate = false;
        if (x.isSelected) {
            removeNodeFromSelection(x, selectedNodes);
        }
    });
}

const recursiveCheckParents = (node, selectedNodes) => {
    if (!node.parentNode) {
        return;
    }

    node.parentNode.isChecked = true;
    mergeChildrenIfAllChecked(node.parentNode, selectedNodes);
    if (isAllDeepChildrenChecked(node.parentNode)) {
        node.parentNode.isIndeterminate = false;
    } else {
        node.parentNode.isIndeterminate = true;
    }
    recursiveCheckParents(node.parentNode, selectedNodes);
}

const isAllDeepChildrenChecked = (node) => node.deepChildren.every(x => x.isChecked);
const isAllDeepChildrenSameCheckValue = (node) => node.deepChildren && node.deepChildren.length && node.deepChildren.reduce((previousValue, currentValue) => previousValue && currentValue.isChecked === node.deepChildren[0].isChecked, true);


const mergeChildrenIfAllChecked = (parentNode, selectedNodes) => {
    if (isAllDeepChildrenChecked(parentNode)) {
        parentNode.children.forEach(x => {
            removeNodeFromSelection(x, selectedNodes);
        })
        parentNode.isSelected = true;
        selectedNodes.push(parentNode.nodeId);
    } else {
        parentNode.isIndeterminate = true;
    }
}


const recursiveSelectAllCheckedSiblings = (node, selectedNodes) => {
    if (!node.parentNode) {
        return;
    }

    if (!node.parentNode.isSelected) {
        recursiveSelectAllCheckedSiblings(node.parentNode, selectedNodes);
    }
    removeNodeFromSelection(node.parentNode, selectedNodes);
    node.parentNode.children.forEach(x => {
        if (x.isChecked && x.nodeId !== node.nodeId) {
            x.isSelected = true;
            selectedNodes.push(x.nodeId);
            checkAllDeepChildren(x, selectedNodes);
        }
    });
}

const recursiveUncheckParents = (node) => {
    if (!node.parentNode) {
        return;
    }

    if (isAllDeepChildrenSameCheckValue(node.parentNode)) {
        if (node.isChecked) {
            node.parentNode.isChecked = true;
            node.parentNode.isIndeterminate = false;
        } else {
            node.parentNode.isChecked = false;
            node.parentNode.isIndeterminate = false;
        }
    } else {
        node.parentNode.isChecked = true;
        node.parentNode.isIndeterminate = true;
    }

    recursiveUncheckParents(node.parentNode);
}

const recursiveExpandParents = (node) => {
    if (!node.parentNode) {
        return;
    }

    // If already expanded, stop recursivity here!
    if (node.parentNode.isExpanded) {
        return;
    }

    node.parentNode.isExpanded = true;
    recursiveExpandParents(node.parentNode);
}

const getSelectedLocationLabel = (selection, dictionary, selectionText, locationNotFoundError, noLocationsSelectedLabel = 'None', isOptional = false) => {
    let locationLabel = "";
    if (isOptional && selection.length === 0) {
        return noLocationsSelectedLabel;
    }
    if (selection.length === 1) {
        if (dictionary[selection[0]]) {
            locationLabel = dictionary[selection[0]].orgPath;
        } else {
            locationLabel = locationNotFoundError;
        }
    } else {
        locationLabel = `${selection.length} ${selectionText}`;
    }

    return locationLabel;
}

/**  
 * @typedef {{
 *  nodeId: number;
 *  fullName: string;
 *  description: string;
 *  orgPath: string;
 *  childrenIds: number[];
 *  parentNodeId: number;
 *  parentNodeOrgPath: string;
 *  isJob: boolean;
 *  isRoot: boolean;
 * }} LocTreeNode  
 * 
 * @param {{
 *      period: Date | string;
 *      locationNodes: any;
 *      selectedNodeIds: number[];
 *      onConfirm: () => {};
 *      isOptional: boolean?;
 * }} param0
 * @returns
 */
const LocationsPicker = ({ period, locationNodes = {}, selectedNodeIds = [], onConfirm = () => { }, isOptional = false }) => {
    const { t } = useTranslation('common');
    const [isShowModal, setIsShowModal] = useState(false);

    const [originalSelection, setOriginalSelection] = useState([...selectedNodeIds]);

    const [selectedNodes, setSelectedNodes] = useState(selectedNodeIds);
    const [locDictionary, setLocDictionary] = useState(locationNodes);

    const [locTrees, setLocTrees] = useState(null);

    useEffect(() => {
        setLocDictionary(locationNodes);

        setSelectedNodes([...new Set(selectedNodeIds)])
        setOriginalSelection([...new Set(selectedNodeIds)]);

        setLocTrees(createLocTrees(locationNodes, selectedNodes));

        selectedNodeIds.forEach(x => {
            if (locDictionary[x]) {
                recursiveExpandParents(locDictionary[x]);
            }
        });
    }, [JSON.stringify(Object.keys(locationNodes))]);


    const handleUpdateExpandable = (node) => {
        node.isExpanded = !node.isExpanded;
        setLocTrees([...locTrees]);
    }

    const handleSelectNode = (node) => {
        const newSelectedNodes = [...selectedNodes];

        node.isChecked = true;
        node.isSelected = true;
        newSelectedNodes.push(node.nodeId);

        checkAllDeepChildren(node, selectedNodes);
        recursiveCheckParents(node, newSelectedNodes);

        setSelectedNodes([...new Set(newSelectedNodes)]);
    }

    const handleUnselectNode = (node) => {
        const newSelectedNodes = [...selectedNodes];

        node.isChecked = false;
        node.isIndeterminate = false;
        if (!node.isSelected) {
            recursiveSelectAllCheckedSiblings(node, newSelectedNodes);
        }
        uncheckAllDeepChildren(node, newSelectedNodes);
        removeNodeFromSelection(node, newSelectedNodes);
        recursiveUncheckParents(node);

        setSelectedNodes([...new Set(newSelectedNodes)]);
    }

    const handleSearchChange = (searchString) => {
        searchString = searchString.toLowerCase().trim();
        const nodesList = Object.values(locDictionary);
        if (searchString === "") {
            nodesList.forEach(x => { x.isSearchHighlight = false; });
        } else {
            var subset = nodesList.filter(x => x.searchHelper.includes(searchString)).map(x => x.nodeId);
            nodesList.filter(x => !subset.includes(x.nodeId)).forEach(x => {
                x.isSearchHighlight = false;
                x.isExpanded = false;
            });
            nodesList.filter(x => subset.includes(x.nodeId)).forEach(x => {
                x.isSearchHighlight = true;
                x.isExpanded = true;
                recursiveExpandParents(x);
            });
        }
        setSelectedNodes([...selectedNodes]);
    }

    const handleCancel = () => {
        setIsShowModal(false);

        const selection = [...new Set(originalSelection)];
        setSelectedNodes(selection)

        setLocTrees(createLocTrees(locDictionary, selection));
        selectedNodeIds.forEach(x => {
            if (locDictionary[x]) {
                recursiveExpandParents(locDictionary[x]);
            }
        });
    }

    const handleConfirm = () => {
        onConfirm(selectedNodes);
        setOriginalSelection([...selectedNodes]);
        setIsShowModal(false);
    }

    const isDisabled = !period || !Object.keys(locationNodes)?.length

    const rendering = (
        <>
            <TextField
                className={`location-picker-text-field`}
                label={t('locationPicker.formLabel')}
                onClick={() => !isDisabled ? setIsShowModal(true) : null}
                disabled={isDisabled}
                value={getSelectedLocationLabel(originalSelection, locDictionary, t('locationPicker.nbSelectedNodeLabel'), t('locationPicker.error.locatioNotFound'), 'None', isOptional)}
                fullWidth
                variant="outlined"
                error={!period || (originalSelection.length === 0 && !isOptional)}
                helperText={!period ? t('locationPicker.error.requiredPeriod') : (originalSelection.length === 0 && !isOptional ? t('locationPicker.error.requiredLocation') : null)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <FontAwesomeIcon icon={['fal', 'sitemap']} color='#357ebd' />
                        </InputAdornment>
                    )
                }}
            />
            <Dialog
                open={isShowModal}
                onClose={handleCancel}
                className='location-picker-modal'
            >
                <DialogTitle
                    id="location-picker-header"
                    className='location-picker-header'
                    children={[
                        <span key="title" className='location-picker-title'>
                            {t('locationPicker.title')}
                        </span>,
                        <span key="period">
                            {`${t('locationPicker.asOf')}: ${period}`}
                        </span>
                    ]}
                >

                </DialogTitle>
                <DialogContent>
                    <div className='location-picker-content'>
                        <div className='location-picker-tree'>
                            <div className='location-picker-search'>
                                <SubmittableSearchBar
                                    className="location-picker-search-bar"
                                    onChange={handleSearchChange}
                                />
                            </div>
                            <div className='location-picker-tree-scroll'>
                                {
                                    locTrees && locTrees.map(root =>
                                        <LocationsTreeNode
                                            key={root.nodeId}
                                            isRoot={true}
                                            node={root}
                                            handleUpdateExpandable={handleUpdateExpandable}
                                            handleSelectNode={handleSelectNode}
                                            handleUnselectNode={handleUnselectNode}
                                        />
                                    )
                                }
                            </div>
                        </div>
                        <div className='location-picker-selection'>
                            <Typography
                                className='location-picker-selection-list-title'
                                color="textSecondary"
                                variant="h5"
                            >
                                {t('locationPicker.selectionList')}
                            </Typography>

                            <div className='location-picker-selection-scroll'>
                                {
                                    selectedNodes && selectedNodes.length ? selectedNodes.map(x => {
                                        const loc = locDictionary[x];
                                        if (loc) {
                                            return <div className="location-picker-selected-location" key={loc.nodeId}>
                                                {loc.orgPath}
                                                <div
                                                    className='location-picker-selection-icon-container'
                                                >
                                                    <SearchIcon
                                                        className='location-picker-selection-icon'
                                                        onClick={() => handleSearchChange(loc.orgPath)}
                                                    />
                                                    <Icon
                                                        path={mdiTrashCanOutline}
                                                        className='location-picker-selection-icon'
                                                        title="Remove location from selection"
                                                        size={1}
                                                        horizontal
                                                        onClick={() => handleUnselectNode(loc)}
                                                    />
                                                </div>
                                            </div>
                                        } else {
                                            return <div className="location-picker-selected-location" key={x}>{t('locationPicker.error.locatioNotFound')}</div>;
                                        }
                                    }) : null
                                }
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        key={'confirm'}
                        color="primary"
                        variant="contained"
                        onClick={handleConfirm}
                    >
                        {t('locationPicker.confirm')}
                    </Button>
                    <Button
                        key={'cancel'}
                        color="secondary"
                        variant="contained"
                        onClick={handleCancel}
                    >
                        {t('locationPicker.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    return rendering;
}

export default LocationsPicker;
